import { FC, useCallback, useState } from "react";
import { ClientsDropdown } from "../../../components/ClientsDropdown";
import { AvatarClient } from "../../../components";
import FileUploadCard from "./FileUploadCard";
import TruckingJobForm from "./TruckingJobForm";
import AdvancedSettings from "./AdvancedSettings";
import RecentClients from "./RecentClients";
import { IClient, ILeg } from "@/types";
import FileProcessingPlaceHolder from "./FileProcessingPlaceHolder";
import FileProcessor from "@/components/FileProcessor";
import FilePreview from "./FilePreview";
import { generateLeg } from "@/utils/data-generators";
import { Button } from "@/shared/Button";

// Types
interface TruckingFormData {
  client: IClient;
  newClient?: boolean;
  [key: string]: any;
}

interface TruckingWorkFlowProps {
  // Form state
  form: TruckingFormData;
  updateForm: (data: { id: string; value: any }) => void;
  errors: Record<string, string>;

  // Client handling
  recentClients: IClient[];
  handleSelectClient: (client: IClient) => void;
  handleCancel: () => void;

  // File upload
  triggerFileInput: () => void;
  handleDirectFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  showFileUpload?: boolean;
  fileUploadStep?: "upload" | "form";
  selectedFile?: File | null;
  getParserConfig?: () => any;
  appType?: "trucking" | "service";
  handleFileDataParsed?: (data: any) => void;
  handleFileError?: (error: any) => void;
  setShowFileUpload?: (show: boolean) => void;
  setFileUploadStep?: (step: "upload" | "form") => void;
  setParsedFileData?: (data: any) => void;
  setDataSelectionMetadata?: (metadata: any) => void;
  parsedFileData?: any;
  onSheetsParsed?: (sheets: any) => void;

  // Legs management
  legs: ILeg[];
  setLegs: (legs: ILeg[] | ((prevLegs: ILeg[]) => ILeg[])) => void;

  // Form display control
  showFullForm: boolean;
  forceRenderCounter: number;
}

interface TruckingJobFormProps {
  form: TruckingFormData;
  handleInputChange: (data: { id: string; value: any }) => void;
  handleDateChange: (date: Date | null) => void;
  handleRateChange: (id: string, value: number) => void;
  legs: ILeg[];
  clientOriginLocation: any;
  updateLegs: (legs: ILeg[] | ((prevLegs: ILeg[]) => ILeg[])) => void;
  handleAddLeg: () => void;
  handleLegUpdate: (id: string, value: any) => void;
  renderKey: (index: number) => string;
  setFormInit: (initData: any, index: number) => void;
  prepareLegDataForLegCreate: (leg: ILeg, index: number) => ILeg;
  applyCalculatedTotalsToLeg: (leg: ILeg, index: number) => void;
  jobTotals: {
    totalMiles: number;
    totalWeight: number;
    totalBaseCharges: number;
    totalFuelSurcharges: number;
    totalDropCharges: number;
    totalCharges: number;
  };
  initialData?: {
    baseRate?: number;
    fuelRate?: number;
    flatRate?: number;
    useFlatRate?: boolean;
    [key: string]: any;
  };
  setLegs: (legs: ILeg[] | ((prevLegs: ILeg[]) => ILeg[])) => void;
}

// Sub-components
const ClientSelectionSection: FC<{
  form: TruckingFormData;
  errors: Record<string, string>;
  updateForm: (data: { id: string; value: any }) => void;
  handleSelectClient: (client: IClient) => void;
  recentClients: IClient[];
}> = ({ form, errors, updateForm, handleSelectClient, recentClients }) => (
  <div className="mb-6">
    <ClientsDropdown
      onClick={(e: IClient) => handleSelectClient(e)}
      onChange={(e: IClient) => handleSelectClient(e)}
      value={form.client}
      form={form}
      errors={errors}
      updateForm={updateForm}
    />
    {!form.newClient && recentClients.length > 0 && (
      <RecentClients
        clients={recentClients}
        onSelectClient={handleSelectClient}
      />
    )}
  </div>
);

const FileUploadSection: FC<{
  fileUploadStep: "upload" | "form";
  selectedFile: File | null;
  getParserConfig: () => any;
  appType: "trucking" | "service";
  handleFileDataParsed: (data: any) => void;
  handleFileError: (error: any) => void;
  setShowFileUpload: (show: boolean) => void;
  setFileUploadStep: (step: "upload" | "form") => void;
  setParsedFileData: (data: any) => void;
  setLegs: (legs: ILeg[] | ((prevLegs: ILeg[]) => ILeg[])) => void;
  setDataSelectionMetadata: (metadata: any) => void;
  form: TruckingFormData;
  updateForm: (data: { id: string; value: any }) => void;
  legs: ILeg[];
  parsedFileData: any;
  forceRenderCounter: number;
  onSheetsParsed?: (sheets: any) => void;
}> = ({
  fileUploadStep,
  selectedFile,
  getParserConfig,
  appType,
  handleFileDataParsed,
  handleFileError,
  setShowFileUpload,
  setFileUploadStep,
  setParsedFileData,
  setLegs,
  setDataSelectionMetadata,
  form,
  updateForm,
  legs,
  parsedFileData,
  forceRenderCounter,
  onSheetsParsed,
}) => {
  const [fileSectionCollapsed, setFileSectionCollapsed] = useState(false);
  const [legCreateLayout, setLegCreateLayout] = useState(true);

  const handleFormInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | { id: string; value: any }) => {
      if ("target" in e) {
        // Handle HTML input event
        const updateData = {
          id: e.target.id,
          value:
            e.target.type === "checkbox" ? e.target.checked : e.target.value,
        };
        updateForm(updateData);
      } else {
        // Handle direct object update
        updateForm(e);
      }
    },
    []
  );

  const handleAddLeg = useCallback(() => {
    if (parsedFileData?.legs?.length > 0) {
      // console.log("handleAddLeg", parsedFileData.legs);
      // Simply pass the extracted legs directly
      setLegs(parsedFileData.legs);
    } else {
      // Create single empty leg if no extracted legs
      const newLeg: ILeg = generateLeg(form, legs);
      setLegs((prevLegs) => [...prevLegs, newLeg]);
    }
  }, [parsedFileData?.legs]);

  return (
    <div className="space-y-6">
      {fileUploadStep === "upload" && (
        <div className="space-y-4">
          <FileProcessingPlaceHolder selectedFile={selectedFile} />
          <div className="hidden">
            <FileProcessor
              config={getParserConfig()}
              appType={appType}
              onDataParsed={handleFileDataParsed}
              onError={handleFileError}
              onFileSelect={() => {}}
              showPreview={false}
              showFileViewer={false}
              showFileViewerHeader={false}
              viewerOnly={false}
              className=""
              acceptedFileTypes={[".csv", ".xlsx", ".xls", ".pdf"]}
              selectedFile={selectedFile}
              autoProcess={!!selectedFile}
              onSheetsParsed={onSheetsParsed}
            >
              {null}
            </FileProcessor>
          </div>
        </div>
      )}

      {fileUploadStep === "form" && parsedFileData && (
        <>
          {/* <div className="mb-4"> */}
          <Button
            type="light" // or "default" or whatever fits your Button API
            size="sm"
            onClick={() => {
              setFileSectionCollapsed((prev) => {
                const collapsed = !prev;
                setLegCreateLayout(!collapsed);
                return collapsed;
              });
            }}
          >
            {fileSectionCollapsed
              ? `Show ${selectedFile?.name} Preview`
              : `Hide ${selectedFile?.name} Preview`}
          </Button>
          {/* </div> */}
          <div>
            <div
              className={`grid gap-8 ${
                selectedFile && !fileSectionCollapsed
                  ? "grid-cols-1 xl:grid-cols-2"
                  : "grid-cols-1"
              }`}
            >
              {selectedFile && !fileSectionCollapsed && (
                <div className="space-y-4">
                  {/* <FilePreview
                  selectedFile={selectedFile}
                  onClick={() => {
                    setShowFileUpload(false);
                    setFileUploadStep("upload");
                    setParsedFileData(null);
                    setLegs([]);
                    setDataSelectionMetadata(null);
                  }}
                /> */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden h-[600px]">
                    <FileProcessor
                      config={getParserConfig()}
                      appType={appType}
                      onDataParsed={() => {}}
                      onError={handleFileError}
                      onFileSelect={() => {}}
                      showPreview={false}
                      showFileViewer={true}
                      showFileViewerHeader={false}
                      viewerOnly={true}
                      className="h-full"
                      acceptedFileTypes={[".csv", ".xlsx", ".xls", ".pdf"]}
                      selectedFile={selectedFile}
                      autoProcess={true}
                      onSheetsParsed={onSheetsParsed}
                    >
                      {null}
                    </FileProcessor>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                <TruckingJobForm
                  legCreateLayout={legCreateLayout}
                  key={`trucking-form-${forceRenderCounter}`}
                  form={form}
                  handleInputChange={handleFormInputChange}
                  handleDateChange={(date) => {
                    // console.log("handleDateChange", date);
                    updateForm({ id: "transactionDate", value: date });
                  }}
                  handleRateChange={(id, value) => {
                    // console.log("handleRateChange", id, value);
                    updateForm({ id, value });
                  }}
                  legs={legs}
                  setLegs={setLegs}
                  clientOriginLocation={form.clientOriginLocation}
                  updateLegs={(legs) => {
                    // console.log("updateLegs", legs);
                    setLegs(legs);
                  }}
                  handleAddLeg={handleAddLeg}
                  handleLegUpdate={(id, value) => {
                    // console.log("handleLegUpdate", id, value);
                    // updateForm({ id, value });
                  }}
                  renderKey={(index) => `leg-${index}`}
                  setFormInit={(initData, index) => {
                    // console.log("Setting form init data:", initData, index);
                  }}
                  prepareLegDataForLegCreate={(leg) => leg}
                  initialData={
                    {}
                    // parsedFileData?.selectedData
                    //   ? {
                    //       // Pass job-level rate information to legs
                    //       baseRate: parsedFileData.selectedData.baseRate,
                    //       fuelRate: parsedFileData.selectedData.fuelRate,
                    //       flatRate: parsedFileData.selectedData.flatRate,
                    //       useFlatRate: parsedFileData.selectedData.useFlatRate,
                    //       // Pass any other extracted data that should be used for legs
                    //       ...parsedFileData.selectedData,
                    //       legs: parsedFileData.legs,
                    //     }
                    //   : undefined
                  }
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const SelectedClientSection: FC<{
  client: IClient;
  onCancel: () => void;
  triggerFileInput: () => void;
  handleDirectFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  showFullForm: boolean;
  showFileUpload: boolean;
  form: TruckingFormData;
  updateForm: (data: { id: string; value: any }) => void;
  legs: ILeg[];
  setLegs: (legs: ILeg[] | ((prevLegs: ILeg[]) => ILeg[])) => void;
  forceRenderCounter: number;
  parsedFileData?: any;
}> = ({
  client,
  onCancel,
  triggerFileInput,
  handleDirectFileSelect,
  showFullForm,
  showFileUpload,
  form,
  updateForm,
  legs,
  setLegs,
  forceRenderCounter,
  parsedFileData,
}) => {
  // Handler functions for TruckingJobForm
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | { id: string; value: any }) => {
      // console.log("handleInputChange", e);
      if ("target" in e) {
        // Handle HTML input event
        const updateData = {
          id: e.target.id,
          value:
            e.target.type === "checkbox" ? e.target.checked : e.target.value,
        };
        updateForm(updateData);
      } else {
        // Handle direct object update
        updateForm(e);
      }
    },
    [updateForm]
  );

  const handleDateChange = useCallback(
    (date: Date | null) => {
      updateForm({
        id: "transactionDate", // Trucking uses transactionDate
        value: date,
      });
    },
    [updateForm]
  );

  const handleRateChange = useCallback(
    (id: string, value: number) => {
      updateForm({ id, value });
    },
    [updateForm]
  );

  return (
    <>
      <div className="mb-5">
        <AvatarClient selected onCancel={onCancel} {...client} />
      </div>
      <FileUploadCard
        triggerFileInput={triggerFileInput}
        handleDirectFileSelect={handleDirectFileSelect}
      />
      {showFullForm && (
        <TruckingJobForm
          key={`trucking-form-${forceRenderCounter}`}
          form={form}
          handleInputChange={handleInputChange}
          handleDateChange={handleDateChange}
          handleRateChange={handleRateChange}
          legs={legs}
          clientOriginLocation={form.clientOriginLocation}
          updateLegs={setLegs}
          handleAddLeg={() => {
            if (parsedFileData?.legs?.length > 0) {
              // Simply pass the extracted legs directly
              setLegs(parsedFileData.legs);
            } else {
              // Create single empty leg if no extracted legs
              const newLeg: ILeg = generateLeg(form, legs);
              setLegs((prevLegs) => [...prevLegs, newLeg]);
            }
          }}
          handleLegUpdate={(id, value) => {
            updateForm({ id, value });
          }}
          renderKey={(index) => `leg-${index}`}
          setFormInit={(initData, index) => {
            // console.log("Setting form init data:", initData, index);
          }}
          prepareLegDataForLegCreate={(leg) => leg}
          applyCalculatedTotalsToLeg={(leg, index) => {
            // console.log("Applying calculated totals to leg:", leg, index);
          }}
          jobTotals={{
            totalMiles: 0,
            totalWeight: 0,
            totalBaseCharges: 0,
            totalFuelSurcharges: 0,
            totalDropCharges: 0,
            totalCharges: 0,
          }}
          initialData={
            parsedFileData?.selectedData
              ? {
                  // Pass job-level rate information to legs
                  baseRate: parsedFileData.selectedData.baseRate,
                  fuelRate: parsedFileData.selectedData.fuelRate,
                  flatRate: parsedFileData.selectedData.flatRate,
                  useFlatRate: parsedFileData.selectedData.useFlatRate,
                  // Pass any other extracted data that should be used for legs
                  ...parsedFileData.selectedData,
                  legs: parsedFileData.legs,
                }
              : undefined
          }
          setLegs={setLegs}
        />
      )}
      {!showFileUpload && !showFullForm && (
        <AdvancedSettings
          defaultOpen={true}
          form={form}
          updateForm={updateForm}
        />
      )}
    </>
  );
};

const TruckingWorkFlow: FC<TruckingWorkFlowProps> = ({
  form,
  updateForm,
  errors,
  recentClients,
  handleSelectClient,
  handleCancel,
  triggerFileInput,
  handleDirectFileSelect,
  showFileUpload = false,
  legs,
  setLegs,
  fileUploadStep = "upload",
  selectedFile = null,
  getParserConfig = () => ({}),
  appType = "trucking",
  handleFileDataParsed = () => {},
  handleFileError = () => {},
  setShowFileUpload = () => {},
  setFileUploadStep = () => {},
  setParsedFileData = () => {},
  setDataSelectionMetadata = () => {},
  showFullForm,
  parsedFileData,
  forceRenderCounter,
  onSheetsParsed,
}) => {
  if (showFileUpload) {
    return (
      <FileUploadSection
        fileUploadStep={fileUploadStep}
        selectedFile={selectedFile}
        getParserConfig={getParserConfig}
        appType={appType}
        handleFileDataParsed={handleFileDataParsed}
        handleFileError={handleFileError}
        setShowFileUpload={setShowFileUpload}
        setFileUploadStep={setFileUploadStep}
        setParsedFileData={setParsedFileData}
        setLegs={setLegs}
        setDataSelectionMetadata={setDataSelectionMetadata}
        form={form}
        updateForm={updateForm}
        legs={legs}
        parsedFileData={parsedFileData}
        forceRenderCounter={forceRenderCounter}
        onSheetsParsed={onSheetsParsed}
      />
    );
  }

  return (
    <div className="space-y-6">
      {!form.client?._id ? (
        <ClientSelectionSection
          form={form}
          errors={errors}
          updateForm={updateForm}
          handleSelectClient={handleSelectClient}
          recentClients={recentClients}
        />
      ) : (
        <SelectedClientSection
          client={form.client}
          onCancel={handleCancel}
          triggerFileInput={triggerFileInput}
          handleDirectFileSelect={handleDirectFileSelect}
          showFullForm={showFullForm}
          showFileUpload={showFileUpload}
          form={form}
          updateForm={updateForm}
          legs={legs}
          forceRenderCounter={forceRenderCounter}
          parsedFileData={parsedFileData}
          setLegs={setLegs}
        />
      )}
    </div>
  );
};

export default TruckingWorkFlow;
